import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./briefly.css";
import brieflyBanner from "../assets/briefly-images/briefly-banner.png";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "../contexts/AuthContext";
import { format } from "date-fns";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import MoreActionsMenu from "../components/common/MoreActionsMenu";
 
import Avatar from "../components/common/Avatar";
import MarkdownEditor from "../components/common/MarkdownEditor";
// Icons
const HeartIcon = ({ filled }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="2"
  >
    <title>Like</title>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

// Removed unused icons to satisfy ESLint

const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <title>Save</title>
    <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <title>New Post</title>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

  const SendIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <title>Send</title>
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );

  const ListIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <title>All Notes</title>
      <rect x="4" y="5" width="16" height="14" rx="2"/>
      <path d="M8 9h8M8 13h8" />
    </svg>
  );

  const StarIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <title>Favorites</title>
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  );

  const FolderIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <title>Folder</title>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );

  const BackIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <title>Back</title>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );

// Stable config
const ALLOWED_YEARS = Object.freeze([2025, 2024, 2023]);

const placeholderTexts = [
  "Write it before it fades.",
  "Catch the spark before it dims.",
  "Think. Type. Don't blink.",
  "Capture the chaos, refine later",
  "Thoughts don't knock twice",
  "What would I regret forgetting",
];

const Briefly = () => {
  const [thoughts, setThoughts] = useState([]);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingThoughtId, setEditingThoughtId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [composerDateStr, setComposerDateStr] = useState("");

  // Filters and tagging
  const [filters, setFilters] = useState({ quick: "all", tag: null, year: null });
  const [composerTags, setComposerTags] = useState([]);
  const [composerNewTag, setComposerNewTag] = useState("");
  const [editTags, setEditTags] = useState([]);
  const [editDateStr, setEditDateStr] = useState("");
  const [editNewTag, setEditNewTag] = useState("");

  
  const [thoughtComments, setThoughtComments] = useState({});
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [deletingComment, setDeletingComment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedThoughtId, setSelectedThoughtId] = useState(null);
  const [, setSidebarExpanded] = useState(false);
  const [, setViewMode] = useState("content"); // 'content' or 'comments'
  const { currentUser, isAuthorized } = useAuth();

  // Mobile responsiveness
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  const [mobileView, setMobileView] = useState("list"); // 'list' | 'detail'

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const fetchComments = async (thoughtId) => {
      const commentsQuery = query(
        collection(db, "briefly", thoughtId, "comments"),
        orderBy("createdAt", "desc")
      );

      onSnapshot(commentsQuery, (snapshot) => {
        const comments = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setThoughtComments((prev) => ({
          ...prev,
          [thoughtId]: comments,
        }));
      });
    };

    const q = query(collection(db, "briefly"), orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const thoughtsData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now()),
          editedAt: data.editedAt?.toDate ? data.editedAt.toDate() : data.editedAt ? new Date(data.editedAt) : undefined,
          tags: Array.isArray(data.tags) ? data.tags : [],
        };
      });

      // Sort to put pinned posts at the top
      thoughtsData.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return 0;
      });

      setThoughts(thoughtsData);
      setLoading(false);

      // Fetch comments for each thought
      thoughtsData.forEach((thought) => {
        fetchComments(thought.id);
      });
    });

    return () => unsubscribe();
  }, []);

  const handleContentChange = (e) => {
    setContent(e.target.value);
  };

  const handleEditContentChange = (e) => {
    setEditContent(e.target.value);
  };

  const addComposerTag = () => {
    const tag = composerNewTag.trim();
    if (!tag) return;
    setComposerTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
    setComposerNewTag("");
  };

  const removeComposerTag = (tag) => {
    setComposerTags((prev) => prev.filter((t) => t !== tag));
  };

  const addEditTag = () => {
    const tag = editNewTag.trim();
    if (!tag) return;
    setEditTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
    setEditNewTag("");
  };

  const removeEditTag = (tag) => {
    setEditTags((prev) => prev.filter((t) => t !== tag));
  };

  const getWordCount = (text) => {
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  const handleSubmit = async () => {
    if (!content.trim() || !currentUser) return;

    setSubmitting(true);
    try {
      const customCreatedAt = combineDate(composerDateStr);
      await addDoc(collection(db, "briefly"), {
        content: content.trim(),
        createdAt: customCreatedAt || serverTimestamp(),
        author: {
          uid: currentUser.uid,
          displayName: currentUser.displayName || "Anonymous",
          photoURL: currentUser.photoURL,
        },
        tags: composerTags,
      });

      // Clear form and exit compose mode
      setContent("");
      setIsComposing(false);
      setComposerTags([]);
      setComposerNewTag("");

      // Update placeholder text for next post
      setCurrentPlaceholderIndex(
        (prevIndex) => (prevIndex + 1) % placeholderTexts.length
      );

      toast.success("Posted!");
    } catch (error) {
      console.error("Error posting:", error);
      toast.error("Failed to post");
    } finally {
      setSubmitting(false);
    }
  };

  // Removed unused like handler to satisfy ESLint

  const handleEdit = (thought) => {
    setEditingThoughtId(thought.id);
    setEditContent(thought.content);
    setEditTags(Array.isArray(thought.tags) ? thought.tags : []);
    const baseDate = thought.createdAt ? new Date(thought.createdAt) : new Date();
    setEditDateStr(toDateInput(baseDate));
    
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return;

    try {
      const updates = {
        content: editContent.trim(),
        editedAt: serverTimestamp(),
        ...(editTags ? { tags: editTags } : {}),
      };
      const customCreatedAt = combineDate(editDateStr);
      if (customCreatedAt) updates.createdAt = customCreatedAt;
      await updateDoc(doc(db, "briefly", editingThoughtId), updates);

      setEditingThoughtId(null);
      setEditContent("");
      toast.success("Post updated!");
    } catch (error) {
      console.error("Error updating post:", error);
      toast.error("Failed to update post");
    }
  };

  const handleCancelEdit = () => {
    setEditingThoughtId(null);
    setEditContent("");
    setEditTags([]);
  };

  // Removed unused comment submit handler to satisfy ESLint

  // Removed unused toggleCommentView to satisfy ESLint

  const handleDeleteComment = async (thoughtId, commentId) => {
    try {
      // Delete the comment
      await deleteDoc(doc(db, "briefly", thoughtId, "comments", commentId));

      toast.success("Comment deleted");
      setShowDeleteConfirm(false);
      setDeletingComment(null);
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    }
  };

  // Removed unused delete click handler to satisfy ESLint

  const handleNoteSelect = (thoughtId) => {
    setSelectedThoughtId(thoughtId);
    setViewMode("content"); // Reset to content view when selecting new note
    setIsComposing(false); // Exit compose mode when selecting a note
    if (isMobile) {
      setMobileView("detail");
    }
    // Close sidebar on mobile after selection
    if (window.innerWidth <= 768) {
      setSidebarExpanded(false);
    }
  };

  const handleStartComposing = () => {
    setIsComposing(true);
    setSelectedThoughtId(null); // Clear selected thought
    setViewMode("content");
    const now = new Date();
    setComposerDateStr(toDateInput(now));
    // Close sidebar on mobile after starting compose
    if (window.innerWidth <= 768) {
      setSidebarExpanded(false);
    }
  };

  // Removed unused compose cancel handler to satisfy ESLint

  // Removed unused toggleSidebar to satisfy ESLint

  const getExcerpt = (content) => {
    // Remove markdown formatting for excerpt
    const text = content
      .replace(/#{1,6}\s?/g, "") // Remove headers
      .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold
      .replace(/\*(.*?)\*/g, "$1") // Remove italic
      .replace(/`(.*?)`/g, "$1") // Remove inline code
      .replace(/\[(.*?)\]\(.*?\)/g, "$1") // Remove links
      .replace(/!\[(.*?)\]\(.*?\)/g, "$1") // Remove images
      .replace(/>/g, "") // Remove blockquotes
      .replace(/^\s*[-*+]\s+/gm, "") // Remove list markers
      .replace(/^\s*\d+\.\s+/gm, "") // Remove numbered list markers
      .trim();

    return text.length > 100 ? text.substring(0, 100) + "..." : text;
  };

  const getTitleFromContent = (content) => {
    const lines = content.split(/\n+/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return "Untitled";
    const heading = lines.find((l) => /^#{1,6}\s+/.test(l));
    const raw = heading ? heading.replace(/^#{1,6}\s+/, "") : lines[0];
    const plain = raw
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/`(.*?)`/g, "$1")
      .replace(/\[(.*?)\]\(.*?\)/g, "$1");
    return plain.length > 80 ? plain.slice(0, 80) + "…" : plain;
  };

  const formatMetaDate = (date) =>
    date ? format(date, "d MMM yyyy") : "";

  const pad2 = (n) => String(n).padStart(2, "0");
  const toDateInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const combineDate = (dateStr) => {
    if (!dateStr) return null;
    const combined = new Date(`${dateStr}T00:00`);
    return isNaN(combined.getTime()) ? null : combined;
  };

  // Derived sets: tags and years
  const allTags = useMemo(() => {
    const set = new Set();
    thoughts.forEach((t) => (t.tags || []).forEach((tag) => set.add(tag)));
    return Array.from(set);
  }, [thoughts]);

  const orderedTags = useMemo(() => {
    return allTags.slice().sort((a, b) => a.localeCompare(b));
  }, [allTags]);

  const yearCounts = useMemo(() => {
    const counts = thoughts.reduce((acc, t) => {
      const y = t.createdAt ? new Date(t.createdAt).getFullYear() : new Date().getFullYear();
      acc[y] = (acc[y] || 0) + 1;
      return acc;
    }, {});
    const dynamicYears = new Set([...ALLOWED_YEARS, ...Object.keys(counts).map((k) => Number(k))]);
    return Array.from(dynamicYears)
      .map((y) => ({ year: y, count: counts[y] || 0 }))
      .sort((a, b) => b.year - a.year);
  }, [thoughts]);

  // Hoisted helper so it's safe during render when filters run
  function getUserId() {
    return (
      currentUser?.uid ||
      `anon_${localStorage.getItem("anonymousUserId") || Date.now()}`
    );
  }

  const applyFilters = useCallback((items) => {
    let list = items;
    if (filters.quick === "my" && currentUser) {
      list = list.filter((n) => n.author?.uid === currentUser.uid);
    }
    if (filters.quick === "favorites") {
      // Prefer admin-set favorite flags if present; fallback to per-user likes
      const hasAdminFavoriteFlag = items.some(
        (n) => n?.isFavorite === true || n?.favorite === true
      );
      if (hasAdminFavoriteFlag) {
        list = list.filter((n) => n?.isFavorite === true || n?.favorite === true);
      } else {
        const uid = getUserId();
        list = list.filter((n) => n.likes && n.likes[uid]);
      }
    }
    if (filters.tag) {
      list = list.filter((n) => (n.tags || []).includes(filters.tag));
    }
    if (filters.year) {
      list = list.filter(
        (n) => (n.createdAt ? new Date(n.createdAt).getFullYear() : 0) === filters.year
      );
    }
    // pinned first, then date
    list = list.slice().sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
    return list;
  }, [filters, currentUser, getUserId]);

  const filteredThoughts = useMemo(() => applyFilters(thoughts), [thoughts, applyFilters]);

  // Keep selection valid when filters change
  useEffect(() => {
    if (!selectedThoughtId && filteredThoughts.length > 0) {
      setSelectedThoughtId(filteredThoughts[0].id);
    } else if (selectedThoughtId && !filteredThoughts.find((t) => t.id === selectedThoughtId)) {
      setSelectedThoughtId(filteredThoughts[0]?.id || null);
    }
  }, [filteredThoughts, selectedThoughtId]);

  const isLikedByUser = (thought) => {
    const userId = getUserId();
    return thought.likes && thought.likes[userId];
  };

  const getCommentCount = (thoughtId) => {
    return thoughtComments[thoughtId]?.length || 0;
  };

  const selectedThought = filteredThoughts.find((t) => t.id === selectedThoughtId);

  const charCount = content.length;
  const wordCount = getWordCount(content);

  // Lock page-level scrolling for desktop; allow normal scroll on mobile
  useEffect(() => {
    if (isMobile) return;
    const originalBodyOverflow = document.body.style.overflow;
    const originalDocOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalDocOverflow;
    };
  }, [isMobile]);

  return (
    <div className="briefly-container">
      {isMobile ? (
        <div className="mobile-briefly">
          {mobileView === "list" ? (
            <div className="mobile-list">
              <div className="list-header">
                <div>
                  <h2 className="sidebar-title" style={{ margin: 0 }}>Briefly</h2>
                </div>
              </div>
              <div className="notes-list">
                {loading ? (
                  <div className="note-preview">
                    <p className="note-excerpt">Loading thoughts...</p>
                  </div>
                ) : filteredThoughts.length === 0 ? (
                  <div className="note-preview">
                    <p className="note-excerpt">No notes found for current filters.</p>
                  </div>
                ) : (
                  filteredThoughts.map((thought) => (
                    <div
                      key={thought.id}
                      className={`note-preview ${selectedThoughtId === thought.id ? "selected" : ""}`}
                      onClick={() => handleNoteSelect(thought.id)}
                    >
                      <div className="note-preview-header">
                        <div className="note-title">
                          {getTitleFromContent(thought.content)}
                          {thought.isPinned && <span className="pin-indicator">📌</span>}
                        </div>
                      </div>
                      <p className="note-excerpt">{getExcerpt(thought.content)}</p>
                      <div className="meta-row">
                        <div className="meta-left">
                          <span className="date-badge">{formatMetaDate(thought.createdAt)}</span>
                          {(thought.likeCount ?? 0) > 0 && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                              <HeartIcon filled={isLikedByUser(thought)} />
                              {thought.likeCount}
                            </span>
                          )}
                        </div>
                        <div className="meta-right">
                          {(thought.tags || []).slice(0, 2).map((t) => (
                            <span className="small-badge" key={t}>{t}</span>
                          ))}
                          {thought.tags && thought.tags.length > 2 && (
                            <span className="small-badge">{`+${thought.tags.length - 2}`}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : selectedThought ? (
            <div className="mobile-detail">
              <div className="mobile-detail-header">
                <button className="back-button" onClick={() => setMobileView("list")} title="Back to all notes">
                  <BackIcon />
                </button>
                <div className="mobile-detail-title">{getTitleFromContent(selectedThought.content)}</div>
              </div>
              <div className="content-area">
                <div className="selected-thought-container">
                  <div className="thought-card">
                    {editingThoughtId === selectedThought.id ? (
                      <div className="edit-container">
                        <div className="edit-body">
                          <div style={{ marginBottom: 12 }}>
                            <div className="datetime-row">
                              <div className="section-title">Date</div>
                              <div className="datetime-group">
                                <input
                                  className="datetime-input"
                                  type="date"
                                  value={editDateStr}
                                  onChange={(e) => setEditDateStr(e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="section-title" style={{ marginBottom: 8 }}>Tags</div>
                            <div className="pill-list">
                              {orderedTags.map((tag) => {
                                const active = editTags.includes(tag);
                                return (
                                  <button
                                    key={tag}
                                    className={`pill ${active ? "active" : ""}`}
                                    onClick={() =>
                                      setEditTags((prev) =>
                                        prev.includes(tag)
                                          ? prev.filter((t) => t !== tag)
                                          : [...prev, tag]
                                      )
                                    }
                                  >
                                    {tag}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <MarkdownEditor
                            value={editContent}
                            onChange={handleEditContentChange}
                            placeholder="Edit your post..."
                            minHeight="200px"
                          />
                        </div>
                        <div className="edit-footer">
                          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                            <button className="edit-button cancel" onClick={handleCancelEdit}>Cancel</button>
                            <button className="edit-button save" onClick={handleSaveEdit} disabled={!editContent.trim()}>
                              <SaveIcon />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="view-container">
                        {selectedThought.content && (
                          <div className="thought-content">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {selectedThought.content}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mobile-list">
              <div className="note-preview">
                <p className="note-excerpt">Select a note to view</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="unified-card">
        <div className="unified-left">
          <div className="sidebar-header">
            <div className="sidebar-header-content">
              <div className="sidebar-title-section" style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                {isAuthorized ? (
                  <button className="new-post-button" onClick={handleStartComposing} title="Create new post" aria-label="Create new post">
                    <PlusIcon />
                  </button>
                ) : (
                  <img src={brieflyBanner} alt="JISDEU — in pursuit of meaning" className="sidebar-banner" />
                )}
              </div>
              {/* No separate actions button when admin; plus icon replaces banner */}
            </div>
          </div>

          <div className="sidebar-section">
            <div className="section-title">Quick Links</div>
            <button
              className={`quick-link ${filters.quick === "all" ? "active" : ""}`}
               onClick={() => setFilters({ quick: "all", tag: null, year: null })}
            >
              <span className="quick-link-label">
                <span style={{ color: "var(--color-text-light)", display: "inline-flex", alignItems: "center" }}>
                  <ListIcon />
                </span>
                All Notes
              </span>
            </button>
            <button
              className={`quick-link ${filters.quick === "favorites" ? "active" : ""}`}
              onClick={() => setFilters((f) => ({ ...f, quick: "favorites" }))}
            >
              <span className="quick-link-label">
                <span style={{ color: "var(--color-text-light)", display: "inline-flex", alignItems: "center" }}>
                  <StarIcon />
                </span>
                Favorites
              </span>
            </button>
          </div>

          {orderedTags.length > 0 && (
            <div className="sidebar-section">
              <div className="section-title">Tags</div>
              <div className="tag-list">
                {orderedTags.map((tag) => (
                  <button
                    key={tag}
                    className={`tag-item ${filters.tag === tag ? "active" : ""}`}
                    onClick={() => setFilters((f) => ({ ...f, tag: f.tag === tag ? null : tag }))}
                  >
                    <span>🏷️ {tag}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="sidebar-section" style={{ borderBottom: "none" }}>
            <div className="section-title">Folders</div>
            <div className="folder-list">
              {yearCounts.map(({ year, count }) => (
                <button
                  key={year}
                  className={`folder-item ${filters.year === year ? "active" : ""}`}
                  onClick={() => setFilters((f) => ({ ...f, year: f.year === year ? null : year }))}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--color-text-light)" }}>
                    <FolderIcon /> {year}
                  </span>
                  <span className="badge">{count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="unified-middle">
          <div className="list-header">
            <div>
              <h2 className="sidebar-title" style={{ margin: 0 }}>Briefly</h2>
            </div>
          </div>
          <div className="notes-list">
            {loading ? (
              <div className="note-preview">
                <p className="note-excerpt">Loading thoughts...</p>
              </div>
            ) : filteredThoughts.length === 0 ? (
              <div className="note-preview">
                <p className="note-excerpt">No notes found for current filters.</p>
              </div>
            ) : (
              filteredThoughts.map((thought) => (
                <div
                  key={thought.id}
                  className={`note-preview ${selectedThoughtId === thought.id ? "selected" : ""}`}
                  onClick={() => handleNoteSelect(thought.id)}
                >
                  <div className="note-preview-header">
                    <div className="note-title">
                      {getTitleFromContent(thought.content)}
                      {thought.isPinned && <span className="pin-indicator">📌</span>}
                    </div>
                  </div>
                  <p className="note-excerpt">{getExcerpt(thought.content)}</p>
                  <div className="meta-row">
                    <div className="meta-left">
                      <span className="date-badge">{formatMetaDate(thought.createdAt)}</span>
                      {(thought.likeCount ?? 0) > 0 && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <HeartIcon filled={isLikedByUser(thought)} />
                          {thought.likeCount}
                        </span>
                      )}
                    </div>
                    <div className="meta-right">
                      {(thought.tags || []).slice(0, 2).map((t) => (
                        <span className="small-badge" key={t}>{t}</span>
                      ))}
                      {thought.tags && thought.tags.length > 2 && (
                        <span className="small-badge">{`+${thought.tags.length - 2}`}</span>
                      )}
                      {isAuthorized && (
                        <div onClick={(e) => e.stopPropagation()} style={{ marginLeft: 4 }}>
                          <MoreActionsMenu
                            item={thought}
                            collection="briefly"
                            onEdit={() => handleEdit(thought)}
                            commentCount={getCommentCount(thought.id)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="unified-right">
          <div className="content-area">
            {isComposing ? (
              <div className="compose-card">
                <div className="compose-header">
                  <Avatar
                    src={currentUser?.photoURL}
                    alt={currentUser?.displayName || currentUser?.email}
                    size="40px"
                    fontSize="14px"
                  />
                  <div className="user-name">{currentUser?.displayName || "Anonymous"}</div>
                </div>

                <div className="compose-body">
                  <div className="datetime-row">
                    <div className="section-title">Date</div>
                    <div className="datetime-group">
                      <input
                        className="datetime-input"
                        type="date"
                        value={composerDateStr}
                        onChange={(e) => setComposerDateStr(e.target.value)}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: "0.5rem" }}>
                    <div className="section-title" style={{ marginBottom: 8 }}>Tags</div>
                    <div className="pill-list">
                      {orderedTags.map((tag) => {
                        const active = composerTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            className={`pill ${active ? "active" : ""}`}
                            onClick={() =>
                              setComposerTags((prev) =>
                                prev.includes(tag)
                                  ? prev.filter((t) => t !== tag)
                                  : [...prev, tag]
                              )
                            }
                          >
                            {tag}
                          </button>
                        );
                      })}
                      <span className="inline-input-wrapper" style={{ marginTop: 10 }}>
                        <input
                          className="inline-input"
                          type="text"
                          placeholder="Add a tag"
                          value={composerNewTag}
                          onChange={(e) => setComposerNewTag(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") addComposerTag();
                          }}
                        />
                        <button className="inline-send" onClick={addComposerTag} aria-label="Add tag">
                          <SendIcon />
                        </button>
                      </span>
                    </div>
                  </div>
                  <MarkdownEditor
                    value={content}
                    onChange={handleContentChange}
                    placeholder={placeholderTexts[currentPlaceholderIndex]}
                    minHeight="120px"
                    disabled={submitting}
                  />
                </div>

                <div className="compose-footer">
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <div className="stats-container">
                      <div className="stat-item">
                        <span className="stat-label">Characters:</span>
                        <span className="stat-value">{charCount}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Words:</span>
                        <span className="stat-value">{wordCount}</span>
                      </div>
                    </div>
                    <button className="post-button" onClick={handleSubmit} disabled={submitting || charCount === 0}>
                      {submitting ? "Posting..." : "Post"}
                    </button>
                  </div>
                </div>
              </div>
            ) : selectedThought ? (
              <div className="selected-thought-container">
                <div className="thought-card">
                  {editingThoughtId === selectedThought.id ? (
                    <div className="edit-container">
                      <div className="edit-body">
                        <div style={{ marginBottom: 12 }}>
                          <div className="datetime-row">
                            <div className="section-title">Date</div>
                            <div className="datetime-group">
                              <input
                                className="datetime-input"
                                type="date"
                                value={editDateStr}
                                onChange={(e) => setEditDateStr(e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="section-title" style={{ marginBottom: 8 }}>Tags</div>
                          <div className="pill-list">
                            {orderedTags.map((tag) => {
                              const active = editTags.includes(tag);
                              return (
                                <button
                                  key={tag}
                                  className={`pill ${active ? "active" : ""}`}
                                  onClick={() =>
                                    setEditTags((prev) =>
                                      prev.includes(tag)
                                        ? prev.filter((t) => t !== tag)
                                        : [...prev, tag]
                                    )
                                  }
                                >
                                  {tag}
                                </button>
                              );
                            })}
                          </div>
                          <div className="pill-list">
                            <span className="inline-input-wrapper" style={{ marginTop: 10 }}>
                              <input
                                className="inline-input"
                                type="text"
                                placeholder="Add a tag"
                                value={editNewTag}
                                onChange={(e) => setEditNewTag(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") addEditTag();
                                }}
                              />
                              <button className="inline-send" onClick={addEditTag} aria-label="Add tag">
                                <SendIcon />
                              </button>
                            </span>
                            {editTags.map((tag) => (
                              <span key={tag} className="small-badge" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                {tag}
                                <button
                                  aria-label={`Remove ${tag}`}
                                  className="pill"
                                  onClick={() => removeEditTag(tag)}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                        <MarkdownEditor
                          value={editContent}
                          onChange={handleEditContentChange}
                          placeholder="Edit your post..."
                          minHeight="200px"
                        />
                      </div>
                      <div className="edit-footer">
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                          <button className="edit-button cancel" onClick={handleCancelEdit}>Cancel</button>
                          <button className="edit-button save" onClick={handleSaveEdit} disabled={!editContent.trim()}>
                            <SaveIcon />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="view-container">
                      {selectedThought.content && (
                        <div className="thought-content">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {selectedThought.content}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", color: "var(--color-text-light)", padding: "3rem" }}>
                <h3 style={{ fontSize: "1.25rem", marginBottom: ".5rem", color: "var(--color-text)" }}>Select a note to view</h3>
                <p style={{ fontSize: ".95rem", marginBottom: 0 }}>Choose a note from the sidebar to read and interact with it</p>
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      {showDeleteConfirm && deletingComment && (
        <>
          <div
            className="confirm-backdrop"
            onClick={() => {
              setShowDeleteConfirm(false);
              setDeletingComment(null);
            }}
          />
          <div className="confirm-dialog">
            <h3 className="confirm-title">Delete this comment?</h3>
            <p className="confirm-text">This action cannot be undone. This comment will be permanently deleted.</p>
            <div className="confirm-buttons">
              <button
                className="confirm-button cancel"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingComment(null);
                }}
              >
                Cancel
              </button>
              <button
                className="confirm-button delete"
                onClick={() =>
                  handleDeleteComment(
                    deletingComment.thoughtId,
                    deletingComment.commentId
                  )
                }
              >
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Briefly;
